/**
 * Group settings.
 *
 * One page, five sections, in the order people need them: what the group looks
 * like, what members may do, who is in it, how to let someone else in, and —
 * last, separated, and hard to reach by accident — how to destroy it.
 *
 * Every section is gated on a capability the server sent us. That gating is
 * presentation only: each procedure re-checks the caller's permissions against
 * freshly loaded state, so a hidden section is a courtesy, not a control.
 */

import { useId, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ACCENT_COLORS,
  canAssignRole,
  canRemoveMember,
  emojiSchema,
  groupNameSchema,
} from "@fc/core";
import type {
  AccentColor,
  Capability,
  GroupRole,
  GroupSettings,
} from "@fc/core";
import { errorMessage, trpc, trpcClient } from "../../lib/trpc.js";
import { useGroupKey } from "../../lib/useGroupKey.js";
import { sealGroupKeyForCode } from "../../lib/keyring.js";
import { useAuth } from "../../lib/auth.js";
import { Avatar } from "../../components/ui/Avatar.js";
import { Badge } from "../../components/ui/Badge.js";
import { Button } from "../../components/ui/Button.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { DropdownMenu } from "../../components/ui/DropdownMenu.js";
import type { DropdownMenuItem } from "../../components/ui/DropdownMenu.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Field } from "../../components/ui/Field.js";
import { Input, Textarea } from "../../components/ui/Input.js";
import { SegmentedControl } from "../../components/ui/SegmentedControl.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { Switch } from "../../components/ui/Switch.js";
import { useToast } from "../../components/ui/Toast.js";
import { AccentPicker } from "./CreateGroupDialog.js";
import "./GroupSettingsPage.css";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";
import { HelpRecovery } from "./HelpRecovery.js";
import { SharedLinksSection } from "../share/SharedLinksSection.js";

const ROLE_LABELS: Record<GroupRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
};

export function GroupSettingsPage() {
  const { groupId } = useParams({ from: "/authed/g/$groupId/settings" });
  const group = useQuery(trpc.groups.get.queryOptions({ groupId }));
  /* For the account row at the top, which shows whose session this is. */
  const auth = useAuth();

  // Drives the header's hairline. It only appears once there is something
  // above the fold to separate from — an always-on rule under a bar that is
  // flush with the content below it is just a line.
  const [scrolled, setScrolled] = useState(false);

  const capabilities = useMemo(
    () => new Set<Capability>(group.data?.capabilities ?? []),
    [group.data?.capabilities],
  );

  const data = group.data;

  return (
    <SettingsShell
      groupId={groupId}
      busy={group.isLoading}
      scrolled={scrolled}
      onScrolledChange={setScrolled}
      title={
        data ? (
          <>
            {data.group.emoji && (
              <span aria-hidden="true">
                <EmojiIcon emoji={data.group.emoji} />{" "}
              </span>
            )}
            {data.group.name}
          </>
        ) : (
          "Settings"
        )
      }
    >
      {group.isLoading ? (
        <>
          <div className="fc-settings__section">
            <Skeleton height="20px" width="140px" />
            <Skeleton height="180px" radius="md" />
          </div>
          <div className="fc-settings__section">
            <Skeleton height="20px" width="140px" />
            <Skeleton height="240px" radius="md" />
          </div>
        </>
      ) : group.isError || !data ? (
        <div className="fc-settings__section">
          <p className="fc-settings__error" role="alert">
            {errorMessage(group.error)}
          </p>
          <div className="fc-settings__actions">
            <Button variant="secondary" onClick={() => void group.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : (
        <>
          <HelpRecovery groupId={groupId} />
          <p className="fc-settings__subtitle">
            {data.memberCount} {data.memberCount === 1 ? "member" : "members"} ·
            you are {data.role === "admin" ? "an" : "the"}{" "}
            {ROLE_LABELS[data.role].toLowerCase()}
          </p>

          {/*
            This page is about the group; your name, address and password are
            not. On a phone there is no sidebar, so without this row there is no
            route to them at all.
          */}
          {/*
            On a phone there is no sidebar, so this row is the only route to
            the trash — and the trash is where a photograph deleted by mistake
            is still sitting.
          */}
          <Link
            className="fc-settings__account"
            to="/g/$groupId"
            params={{ groupId }}
            search={{ view: "trash" as const }}
          >
            <span>
              <strong>Trash</strong>
              <span>Deleted photos, before they are gone for good</span>
            </span>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </Link>

          {/*
            You, rather than a menu item about you.

            This said “Your account — your name, email and password”, which is
            accurate and reads as one more setting in a list of group
            settings. Your own face and name at the top says whose session
            this is, which on a shared phone is the question, and makes the
            row obviously about you rather than about the group.
          */}
          <Link className="fc-settings__account" to="/account">
            <Avatar
              name={auth.user?.displayName ?? "?"}
              src={auth.user?.avatarImage ?? undefined}
              size="md"
              decorative
            />
            <span>
              <strong>{auth.user?.displayName ?? "Your account"}</strong>
              <span>
                {auth.user?.email ?? "Your name, email and password"}
              </span>
            </span>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </Link>

          {capabilities.has("group:manage_appearance") && (
            <AppearanceSection
              key={`appearance-${groupId}`}
              groupId={groupId}
              initial={{
                name: data.group.name,
                description: data.group.description ?? "",
                emoji: data.group.emoji ?? "",
                accentColor: toAccentColor(data.group.accentColor),
              }}
            />
          )}

          {capabilities.has("group:manage_settings") && (
            <PermissionsSection
              key={`permissions-${groupId}`}
              groupId={groupId}
              initial={data.settings}
            />
          )}

          {capabilities.has("group:manage_settings") && (
            <RepairSection key={`repair-${groupId}`} groupId={groupId} />
          )}

          <MembersSection
            groupId={groupId}
            capabilities={capabilities}
            myRole={data.role}
          />

          {capabilities.has("member:invite") && (
            <InvitesSection groupId={groupId} />
          )}

          {/*
            Beside invites, because they answer the same question from opposite
            ends: who is being let in, and what is being let out.
          */}
          {capabilities.has("share:create") && (
            <SharedLinksSection groupId={groupId} />
          )}

          {capabilities.has("group:delete") && (
            <DangerZone groupId={groupId} groupName={data.group.name} />
          )}
        </>
      )}
    </SettingsShell>
  );
}

/* ──────────────────────────────── Page frame ──────────────────────────────── */

/**
 * The page's own scroll container, with a header bar pinned to the top of it.
 *
 * The scrolling happens here rather than on `body` deliberately: `.fc-main` is
 * a bounded grid row, so an internally scrolling page keeps the mobile bottom
 * navigation on screen the whole way down instead of pushing it off.
 */
function SettingsShell({
  groupId,
  title,
  busy,
  scrolled,
  onScrolledChange,
  children,
}: {
  groupId: string;
  title: React.ReactNode;
  busy: boolean;
  scrolled: boolean;
  onScrolledChange: (value: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fc-settings"
      aria-busy={busy || undefined}
      onScroll={(event) => onScrolledChange(event.currentTarget.scrollTop > 0)}
    >
      <header
        className="fc-settings__bar"
        data-scrolled={scrolled ? "" : undefined}
      >
        <div className="fc-settings__bar-inner">
          <Link
            className="fc-settings__back"
            to="/g/$groupId"
            params={{ groupId }}
            search={{}}
            aria-label="Back to photos"
          >
            <BackIcon />
          </Link>
          <h1 className="fc-settings__title">{title}</h1>
        </div>
      </header>

      <div className="fc-settings__content">{children}</div>
    </div>
  );
}

/** The stored value is a plain string; anything unrecognised falls back. */
function toAccentColor(value: string): AccentColor {
  return (ACCENT_COLORS as readonly string[]).includes(value)
    ? (value as AccentColor)
    : "violet";
}

/* ───────────────────────────────── Appearance ─────────────────────────────── */

interface AppearanceValues {
  name: string;
  description: string;
  emoji: string;
  accentColor: AccentColor;
}

function AppearanceSection({
  groupId,
  initial,
}: {
  groupId: string;
  initial: AppearanceValues;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [values, setValues] = useState<AppearanceValues>(initial);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dirty =
    values.name !== initial.name ||
    values.description !== initial.description ||
    values.emoji !== initial.emoji ||
    values.accentColor !== initial.accentColor;

  const save = useMutation({
    mutationFn: (input: {
      groupId: string;
      name: string;
      description: string | null;
      emoji: string | null;
      accentColor: AccentColor;
    }) => trpcClient.groups.update.mutate(input),
    onSuccess: async () => {
      // Both the page and the sidebar switcher render this group's identity.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.groups.get.queryKey({ groupId }),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.groups.list.queryKey(),
        }),
      ]);
      toast("Appearance saved.", { variant: "success" });
    },
  });

  function submit(event: React.FormEvent) {
    event.preventDefault();

    const problems: Record<string, string> = {};
    const nameResult = groupNameSchema.safeParse(values.name);
    if (!nameResult.success)
      problems.name = nameResult.error.issues[0]!.message;

    const emoji = values.emoji.trim();
    if (emoji.length > 0) {
      const emojiResult = emojiSchema.safeParse(emoji);
      if (!emojiResult.success)
        problems.emoji = emojiResult.error.issues[0]!.message;
    }

    setFieldErrors(problems);
    if (Object.keys(problems).length > 0) return;

    const description = values.description.trim();
    save.mutate({
      groupId,
      name: values.name.trim(),
      // Empty means "cleared", which is a null on the wire — not an empty
      // string that would later render as a blank line under the group name.
      description: description.length > 0 ? description : null,
      emoji: emoji.length > 0 ? emoji : null,
      accentColor: values.accentColor,
    });
  }

  return (
    <section className="fc-settings__section" aria-labelledby="fc-appearance">
      <div className="fc-settings__section-head">
        <h2 className="fc-settings__section-title" id="fc-appearance">
          Appearance
        </h2>
        <p className="fc-settings__section-hint">
          How this group shows up in everyone&rsquo;s sidebar.
        </p>
      </div>

      <form className="fc-group-form" onSubmit={submit}>
        {save.isError && (
          <p className="fc-group-form__error" role="alert">
            {errorMessage(save.error)}
          </p>
        )}

        <Field label="Name" required error={fieldErrors.name}>
          <Input
            value={values.name}
            maxLength={60}
            onChange={(event) =>
              setValues((current) => ({ ...current, name: event.target.value }))
            }
          />
        </Field>

        <Field label="Description" hint="Optional. Shown to anyone you invite.">
          <Textarea
            rows={2}
            value={values.description}
            maxLength={500}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
          />
        </Field>

        <Field
          label="Emoji"
          hint="Optional. One emoji."
          error={fieldErrors.emoji}
        >
          <Input
            className="fc-group-form__emoji"
            value={values.emoji}
            maxLength={8}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                emoji: event.target.value,
              }))
            }
          />
        </Field>

        <AccentPicker
          value={values.accentColor}
          onChange={(accentColor) =>
            setValues((current) => ({ ...current, accentColor }))
          }
        />

        <div className="fc-settings__actions">
          <Button
            type="submit"
            variant="primary"
            loading={save.isPending}
            disabled={!dirty}
          >
            Save changes
          </Button>
          {dirty && (
            <Button
              variant="ghost"
              onClick={() => setValues(initial)}
              disabled={save.isPending}
            >
              Discard
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}

/* ──────────────────────────────── Permissions ─────────────────────────────── */

interface SettingCopy {
  key: keyof GroupSettings;
  label: string;
  help: string;
}

/**
 * Plain English for each toggle. The wording says what happens, not what the
 * flag is called: "Members can" is the subject of nine of these, and the tenth
 * is explicitly marked as applying to everyone.
 */
const SETTING_COPY: readonly SettingCopy[] = [
  {
    key: "membersCanUpload",
    label: "Add photos and videos",
    help: "Turn this off and only admins can add anything new.",
  },
  {
    key: "membersCanCreateTrips",
    label: "Create trips and albums",
    help: "Lets members organise the library into new trips, not just add to existing ones.",
  },
  {
    key: "membersCanDeleteOwnFiles",
    label: "Delete their own photos",
    help: "Deleted photos go to the trash for 30 days first — nothing disappears immediately.",
  },
  {
    key: "membersCanDeleteOthersFiles",
    label: "Delete anyone's photos",
    help: "Usually off. Turn it on only for a group where everyone tidies up together.",
  },
  {
    key: "membersCanDownload",
    label: "Download photos",
    help: "When off, members can view photos in the app but not save copies.",
  },
  {
    key: "membersCanShareExternally",
    label: "Share links outside the group",
    help: "Share links can be opened by anyone who has the link, including people not in the group.",
  },
  {
    key: "membersCanCreatePosts",
    label: "Write posts",
    help: "Posts are the short notes that appear alongside photos in the group feed.",
  },
  {
    key: "membersCanComment",
    label: "Comment",
    help: "Turn this off for a quiet, photos-only group.",
  },
  {
    key: "membersCanInvite",
    label: "Invite other people",
    help: "When on, any member can create invite codes and links. Invites they create can only ever add ordinary members.",
  },
  {
    key: "allowOriginalDownloads",
    label: "Allow full-quality originals",
    help: "This one applies to everyone, admins included. When off, downloads are the smaller web-quality version.",
  },
];

function PermissionsSection({
  groupId,
  initial,
}: {
  groupId: string;
  initial: GroupSettings;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [settings, setSettings] = useState<GroupSettings>(initial);

  const save = useMutation({
    mutationFn: (change: { key: keyof GroupSettings; value: boolean }) =>
      trpcClient.groups.updateSettings.mutate({
        groupId,
        settings: singleSetting(change.key, change.value),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: trpc.groups.get.queryKey({ groupId }),
      }),
    onError: (error, change) => {
      // Put the switch back where it was: leaving it showing a state the
      // server rejected is worse than the failure itself.
      setSettings((current) => ({ ...current, [change.key]: !change.value }));
      toast(errorMessage(error), { variant: "danger" });
    },
  });

  return (
    <section className="fc-settings__section" aria-labelledby="fc-permissions">
      <div className="fc-settings__section-head">
        <h2 className="fc-settings__section-title" id="fc-permissions">
          What members can do
        </h2>
        <p className="fc-settings__section-hint">
          These switches shape what <strong>members</strong> can do. Owners and
          admins keep every ability regardless — except the last one, which
          applies to everyone. Changes save immediately.
        </p>
      </div>

      <div className="fc-toggles">
        {SETTING_COPY.map((setting) => (
          <ToggleRow
            key={setting.key}
            label={setting.label}
            help={setting.help}
            checked={settings[setting.key]}
            disabled={save.isPending}
            onChange={(value) => {
              setSettings((current) => ({ ...current, [setting.key]: value }));
              save.mutate({ key: setting.key, value });
            }}
          />
        ))}
      </div>
    </section>
  );
}

/** Writing through a union key keeps the patch to the one field that changed. */
function singleSetting(
  key: keyof GroupSettings,
  value: boolean,
): Partial<GroupSettings> {
  const patch: Partial<GroupSettings> = {};
  patch[key] = value;
  return patch;
}

function ToggleRow({
  label,
  help,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  help: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  const uid = useId();
  const labelId = `${uid}label`;
  const helpId = `${uid}help`;

  return (
    <div className="fc-toggle">
      <div className="fc-toggle__text">
        <span className="fc-toggle__label" id={labelId}>
          {label}
        </span>
        <p className="fc-toggle__help" id={helpId}>
          {help}
        </p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-labelledby={labelId}
        aria-describedby={helpId}
      />
    </div>
  );
}

/* ────────────────────────────────── Members ───────────────────────────────── */

interface PendingAction {
  kind: "remove" | "leave" | "transfer";
  userId: string;
  name: string;
}

/* ─────────────────────────────── Repair ──────────────────────────────── */

/**
 * Putting a group back in agreement with itself.
 *
 * Offered because both of the things it fixes are permanent otherwise. The
 * item count is a running total that a half-finished upload batch leaves
 * behind for good, and a photograph that reached storage without its
 * thumbnails renders as "No preview" for ever — the browser's Rebuild button
 * correctly refuses unsealed files, since their previews are the server's to
 * make, and nothing was asking the server to make them.
 */
function RepairSection({ groupId }: { groupId: string }) {
  /*
   * The group key has to be open before any of this can work.
   *
   * Rebuilding a sealed preview means decrypting the original, and the key is
   * opened asynchronously when a group is entered. Pressing Repair before that
   * finished failed every file with “That group has not been opened yet” and
   * reported it as though the photographs were at fault.
   */
  const keyState = useGroupKey(groupId);
  const [result, setResult] = useState<{
    requeued: number;
    previewless: number;
    sealedWithoutPreview: number;
    fileCount: number;
  } | null>(null);

  /** How far through the browser-side half of a repair we are. */
  const [rebuilding, setRebuilding] = useState<{
    done: number;
    total: number;
    failed: number;
    /** The first thing that went wrong, shown rather than swallowed. */
    why?: string;
  } | null>(null);

  /*
   * The half only a browser can do.
   *
   * An encrypted photograph's thumbnails are made from plaintext the server
   * has never held, so no amount of server-side repair can produce them —
   * which is why pressing Repair on a group in that state appeared to do
   * nothing at all. The browser does it here, once, when somebody asks: it
   * fetches each original, decrypts it, resizes it and writes the derivative
   * back, after which the group is permanently fixed and costs a thumbnail
   * like any other.
   *
   * Deliberately here and not in the gallery. Doing it while somebody browses
   * means spending their processor and their data allowance every time they
   * look at a photograph, for ever — and it hammers the signing endpoint hard
   * enough to be rate-limited, which is its own new failure. Once, on request,
   * with a count of what is left.
   *
   * Two at a time, and paced: fetching an original is a metered operation and
   * a few hundred of them arriving together is what trips the limiter.
   */
  const rebuildSealed = async (fileIds: string[]) => {
    if (fileIds.length === 0) return;

    const { rebuildPreview } = await import("../../lib/rebuildPreview.js");
    let done = 0;
    let failed = 0;
    let why: string | undefined;
    setRebuilding({ done: 0, total: fileIds.length, failed: 0 });

    const queue = [...fileIds];
    const worker = async () => {
      for (;;) {
        const fileId = queue.shift();
        if (!fileId) return;
        try {
          await rebuildPreview(groupId, fileId);
        } catch (error) {
          failed += 1;
          if (!why) {
            why =
              error instanceof Error
                ? `${error.name}: ${error.message}`
                : "unknown";
          }
        }
        done += 1;
        setRebuilding({ done, total: fileIds.length, failed, ...(why ? { why } : {}) });
        // A breath between files, so a long pass does not saturate the
        // signing endpoint's budget.
        await new Promise((resolve) => setTimeout(resolve, 120));
      }
    };

    await Promise.all([worker(), worker()]);
  };

  const repair = useMutation({
    mutationFn: () => trpcClient.groups.repair.mutate({ groupId }),
    onSuccess: async (data) => {
      setResult(data);
      // Without the key there is nothing this browser can rebuild, and trying
      // would fail every file with the same unhelpful sentence.
      if (keyState === "ready" || keyState === "unencrypted") {
        await rebuildSealed(data.sealedWithoutPreviewIds);
      }
    },
  });

  return (
    <section className="fc-settings__section" aria-labelledby="fc-repair">
      <div className="fc-settings__section-head">
        <h2 className="fc-settings__section-title" id="fc-repair">
          Repair
        </h2>
        <p className="fc-settings__section-hint">
          Recounts this group&rsquo;s items and storage, and asks the server to make
          any missing previews again. Safe to run at any time.
        </p>
      </div>

      <div className="fc-settings__actions">
        <Button
          variant="secondary"
          loading={repair.isPending || keyState === "opening"}
          disabled={keyState === "opening"}
          onClick={() => repair.mutate()}
        >
          Repair this group
        </Button>
      </div>

      {repair.isError && (
        <p className="fc-settings__error" role="alert">
          {errorMessage(repair.error)}
        </p>
      )}

      {rebuilding && (
        <p className="fc-settings__section-hint" role="status">
          Rebuilding previews in this browser: {rebuilding.done} of{" "}
          {rebuilding.total}
          {rebuilding.failed > 0 && ` — ${String(rebuilding.failed)} could not be rebuilt`}
          . Leave this page open until it finishes.
          {rebuilding.why && (
            <>
              {" "}
              <span className="fc-settings__code">{rebuilding.why}</span>
            </>
          )}
        </p>
      )}

      {/*
        Said, not skipped.

        Rebuilding a sealed preview needs the group key, and a browser that has
        not opened this group has none. Doing nothing quietly here is how the
        button earned its reputation for doing nothing at all.
      */}
      {result &&
        result.sealedWithoutPreview > 0 &&
        keyState !== "ready" &&
        keyState !== "unencrypted" && (
          <p className="fc-settings__section-hint" role="status">
            This browser cannot rebuild those previews because the group is not
            open here. Open the gallery first — signing in again if it asks —
            then come back and press Repair.{" "}
            <span className="fc-settings__code">KEY-{keyState.toUpperCase()}</span>
          </p>
        )}

      {result && (
        <p className="fc-settings__section-hint" role="status">
          {result.fileCount} items counted.{" "}
          {result.previewless > 0 &&
            `${String(result.requeued)} of ${String(result.previewless)} missing previews queued — they appear as the server finishes them. `}
          {/*
            Said out loud, because this is the case where Repair legitimately
            does nothing and used to say nothing either.
          */}
          {result.sealedWithoutPreview > 0
            ? `${String(result.sealedWithoutPreview)} encrypted photos had no preview. The server cannot make those — only a browser holding the key can — so this browser is rebuilding them now.`
            : result.previewless === 0
              ? "Every photo already has a preview."
              : ""}
        </p>
      )}
    </section>
  );
}
function MembersSection({
  groupId,
  capabilities,
  myRole,
}: {
  groupId: string;
  capabilities: ReadonlySet<Capability>;
  myRole: GroupRole;
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const members = useQuery(trpc.groups.members.queryOptions({ groupId }));
  const [pending, setPending] = useState<PendingAction | null>(null);

  const myUserId = auth.user?.id ?? "";

  /* `groups.members` already excludes anybody who has left. */
  const active = members.data ?? [];
  const ownerless =
    active.length > 0 && !active.some((member) => member.role === "owner");

  /*
   * Admins first, and only if there are none does a member qualify — the
   * same ordering the server enforces, mirrored here so the button is not
   * offered to somebody it would refuse.
   */
  const admins = active.filter((member) => member.role === "admin");
  const eligible = admins.length > 0 ? admins : active;
  const canClaim =
    ownerless && eligible.some((member) => member.userId === myUserId);

  const claim = useMutation({
    mutationFn: () => trpcClient.groups.claimOwnership.mutate({ groupId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.members.queryKey({ groupId }),
      });
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.get.queryKey({ groupId }),
      });
      toast("You own this group now.", { variant: "success" });
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  const changeRole = useMutation({
    mutationFn: (input: { userId: string; role: GroupRole }) =>
      trpcClient.groups.updateMember.mutate({ groupId, ...input }),
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.members.queryKey({ groupId }),
      });
      toast(`Role changed to ${ROLE_LABELS[input.role].toLowerCase()}.`, {
        variant: "success",
      });
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  const transfer = useMutation({
    mutationFn: (input: { userId: string }) =>
      trpcClient.groups.transferOwnership.mutate({
        groupId,
        userId: input.userId,
      }),
    onSuccess: async () => {
      setPending(null);
      /*
       * Both lists, because both changed: the member roles here, and the badge
       * on the home grid that now says Admin rather than Owner.
       */
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.members.queryKey({ groupId }),
      });
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.list.queryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.get.queryKey({ groupId }),
      });
      toast("The group has a new owner.", { variant: "success" });
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  const remove = useMutation({
    mutationFn: (input: { userId: string }) =>
      trpcClient.groups.removeMember.mutate({ groupId, userId: input.userId }),
    onSuccess: async (_result, input) => {
      setPending(null);
      if (input.userId === myUserId) {
        // We are no longer a member, so every query for this group would now
        // fail — leave the group's screens entirely.
        await queryClient.invalidateQueries({
          queryKey: trpc.groups.list.queryKey(),
        });
        await navigate({ to: "/" });
        toast("You've left the group.", { variant: "success" });
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.members.queryKey({ groupId }),
      });
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.get.queryKey({ groupId }),
      });
      toast("Member removed.", { variant: "success" });
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  return (
    <section className="fc-settings__section" aria-labelledby="fc-members">
      <div className="fc-settings__section-head">
        <h2 className="fc-settings__section-title" id="fc-members">
          Members
        </h2>
      </div>

      {/*
        A group with nobody in charge, and the way out of it.

        Reachable by an owner leaving before that was stopped, or by their
        account being lost. Nobody outranks an owner, so nothing inside the
        group could appoint one and the only route was a platform
        administrator with the recovery shares — for a decision the members
        are perfectly placed to make themselves.
      */}
      {!members.isLoading && ownerless && (
        <div className="fc-settings__error" role="status">
          <p>
            This group has no owner. Everything still works — admins can add
            and remove people, and nobody has lost any photos. What is missing
            is somebody who can delete the group or hand it on.
          </p>
          {canClaim ? (
            <Button
              variant="primary"
              loading={claim.isPending}
              onClick={() => claim.mutate()}
            >
              Take ownership
            </Button>
          ) : (
            <p>An admin of this group has to take it.</p>
          )}
        </div>
      )}

      {members.isLoading ? (
        <div className="fc-members" aria-hidden="true">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} height="var(--fc-row-height)" radius="sm" />
          ))}
        </div>
      ) : members.isError ? (
        <p className="fc-settings__error" role="alert">
          {errorMessage(members.error)}
        </p>
      ) : (
        <ul className="fc-members">
          {(members.data ?? []).map((member) => {
            const isSelf = member.userId === myUserId;
            const name =
              member.user?.displayName ??
              member.user?.username ??
              "Former member";
            const items = menuItemsForMember({
              capabilities,
              myRole,
              myUserId,
              memberRole: member.role,
              memberUserId: member.userId,
              onChangeRole: (role) =>
                changeRole.mutate({ userId: member.userId, role }),
              onTransfer: () =>
                setPending({
                  kind: "transfer",
                  userId: member.userId,
                  name,
                }),
              onRemove: () =>
                setPending({
                  kind: isSelf ? "leave" : "remove",
                  userId: member.userId,
                  name,
                }),
            });

            return (
              <li className="fc-member" key={member.userId}>
                {/* The name is right beside it, so the avatar is decoration. */}
                <Avatar name={name} size="md" decorative />

                <div className="fc-member__body">
                  <span className="fc-member__name">
                    {name}
                    {isSelf && <span className="fc-member__you">You</span>}
                  </span>
                  <span className="fc-member__meta fc-tabular">
                    Joined {formatDate(member.joinedAt)}
                  </span>
                </div>

                <Badge variant={member.role === "owner" ? "accent" : "neutral"}>
                  {ROLE_LABELS[member.role]}
                </Badge>

                {items.length > 0 && (
                  <DropdownMenu
                    align="end"
                    menuLabel={`Actions for ${name}`}
                    items={items}
                    trigger={
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        aria-label={`Actions for ${name}`}
                        leadingIcon={<MoreIcon />}
                      />
                    }
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pending && (
        <Dialog
          open
          onClose={() => setPending(null)}
          title={
            pending.kind === "leave"
              ? "Leave this group?"
              : pending.kind === "transfer"
                ? `Make ${pending.name} the owner?`
                : `Remove ${pending.name}?`
          }
          description={
            pending.kind === "leave"
              ? "You'll lose access to the group's photos. Anything you uploaded stays with the group."
              : pending.kind === "transfer"
                ? /*
                   * States what the giver loses, which is the part people
                   * are actually deciding about — and that it is not a
                   * one-way door, because being told it is irreversible
                   * when it is not makes every other warning worth less.
                   */
                  `You'll become an admin: you keep everything except the ` +
                  `ability to hand the group on or delete it. ${pending.name} ` +
                  `can give it back.`
                : `${pending.name} will lose access straight away. The photos they uploaded stay in the group.`
          }
          footer={
            <>
              <Button variant="ghost" onClick={() => setPending(null)}>
                Cancel
              </Button>
              {pending.kind === "transfer" ? (
                <Button
                  variant="primary"
                  loading={transfer.isPending}
                  onClick={() => transfer.mutate({ userId: pending.userId })}
                >
                  Make owner
                </Button>
              ) : (
                <Button
                  variant="danger"
                  loading={remove.isPending}
                  onClick={() => remove.mutate({ userId: pending.userId })}
                >
                  {pending.kind === "leave" ? "Leave group" : "Remove"}
                </Button>
              )}
            </>
          }
        />
      )}
    </section>
  );
}

/**
 * Builds one member row's menu. The capability checks are the same pure
 * functions the server uses, so a menu item never appears for something the
 * mutation would reject.
 */
function menuItemsForMember({
  capabilities,
  myRole,
  myUserId,
  memberRole,
  memberUserId,
  onChangeRole,
  onTransfer,
  onRemove,
}: {
  capabilities: ReadonlySet<Capability>;
  myRole: GroupRole;
  myUserId: string;
  memberRole: GroupRole;
  memberUserId: string;
  onChangeRole: (role: GroupRole) => void;
  onTransfer: () => void;
  onRemove: () => void;
}): DropdownMenuItem[] {
  // Your own row never offers role changes or removal — the only thing you can
  // do to yourself here is leave.
  if (memberUserId === myUserId) {
    return [{ label: "Leave group", danger: true, onSelect: onRemove }];
  }

  const items: DropdownMenuItem[] = [];

  for (const role of ["admin", "member"] as const) {
    if (role === memberRole) continue;
    if (!canAssignRole(capabilities, myRole, memberRole, role)) continue;
    items.push({
      label: role === "admin" ? "Make an admin" : "Change to member",
      onSelect: () => onChangeRole(role),
    });
  }

  /*
   * Handing the group over.
   *
   * Owner-only, and not driven by `canAssignRole` — that refuses to assign
   * owner at all, on purpose, because an admin who could appoint themselves
   * would make the role meaningless. Last in the list and separated from
   * the role changes above it, because it is not one of them: it changes
   * *your* role too.
   */
  if (myRole === "owner" && memberRole !== "owner") {
    items.push({
      label: "Make owner…",
      onSelect: onTransfer,
    });
  }

  if (
    canRemoveMember(capabilities, myRole, myUserId, memberRole, memberUserId)
  ) {
    items.push({
      label: "Remove from group",
      danger: true,
      onSelect: onRemove,
    });
  }

  return items;
}

/* ────────────────────────────────── Invites ───────────────────────────────── */

type MaxUsesChoice = "1" | "5" | "10" | "unlimited";
type ExpiryChoice = "never" | "24" | "168" | "720";

const MAX_USES: Record<MaxUsesChoice, number | null> = {
  "1": 1,
  "5": 5,
  "10": 10,
  unlimited: null,
};

const EXPIRY_HOURS: Record<ExpiryChoice, number | null> = {
  never: null,
  "24": 24,
  "168": 7 * 24,
  "720": 30 * 24,
};

interface CreatedInvite {
  kind: "code" | "link";
  code: string;
  url: string;
}

/**
 * What a person is actually given. A link invite's stored value is a token —
 * pasting that into a chat helps nobody, so it is shown and copied as the join
 * URL it belongs in.
 */
function inviteShareText(invite: {
  kind: "code" | "link";
  code: string;
}): string {
  if (invite.kind !== "link") return invite.code;
  return `${window.location.origin}/join/${encodeURIComponent(invite.code)}`;
}

function InvitesSection({ groupId }: { groupId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invites = useQuery(trpc.groups.invites.queryOptions({ groupId }));

  const [maxUses, setMaxUses] = useState<MaxUsesChoice>("1");
  const [expiry, setExpiry] = useState<ExpiryChoice>("168");
  const [created, setCreated] = useState<CreatedInvite | null>(null);

  const create = useMutation({
    /*
     * Two calls, because of an ordering problem worth stating.
     *
     * The group key travels inside the invite, sealed under the code — and the
     * code is issued by the server, so it cannot be sealed until the invite
     * exists. Without the second call the invite carries no key, and everybody
     * who joins with it lands in a group where every photograph is locked to
     * them until some other member happens to open the group. That is what was
     * happening: the API had always been ready to store the key and the client
     * simply never sent one.
     */
    mutationFn: async (input: { kind: "code" | "link" }) => {
      const invite = await trpcClient.groups.createInvite.mutate({
        groupId,
        kind: input.kind,
        maxUses: MAX_USES[maxUses],
        expiresInHours: EXPIRY_HOURS[expiry],
      });

      try {
        const sealed = await sealGroupKeyForCode(groupId, invite.code);
        await trpcClient.groups.sealInvite.mutate({
          groupId,
          inviteId: invite.inviteId,
          ...sealed,
        });
      } catch {
        /*
         * The invite still works — it just falls back to the old behaviour of
         * waiting for a member to hand the key over. Swallowed rather than
         * failing the whole thing, because a group with no key at all reaches
         * here too and has nothing to seal.
         */
      }

      return invite;
    },
    onSuccess: async (result, input) => {
      setCreated({ kind: input.kind, code: result.code, url: result.url });
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.invites.queryKey({ groupId }),
      });
    },
  });

  const revoke = useMutation({
    mutationFn: (input: { inviteId: string; code: string }) =>
      trpcClient.groups.revokeInvite.mutate({
        groupId,
        inviteId: input.inviteId,
      }),
    onSuccess: async (_result, input) => {
      // Don't leave a freshly revoked code sitting on screen looking usable.
      setCreated((current) => (current?.code === input.code ? null : current));
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.invites.queryKey({ groupId }),
      });
      toast("Invite revoked. It can't be used again.", { variant: "success" });
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${what} copied.`, { variant: "success" });
    } catch {
      // Clipboard access can be denied outright; say so rather than pretending
      // it worked, because the user will paste nothing and blame themselves.
      toast(
        "Couldn't copy automatically — select the code and copy it yourself.",
        {
          variant: "danger",
        },
      );
    }
  }

  return (
    <section className="fc-settings__section" aria-labelledby="fc-invites">
      <div className="fc-settings__section-head">
        <h2 className="fc-settings__section-title" id="fc-invites">
          Invites
        </h2>
        <p className="fc-settings__section-hint">
          A code is easy to read out over the phone. A link is easier to send in
          a message. Both do the same thing.
        </p>
      </div>

      <div className="fc-invites__controls">
        <div className="fc-invites__control">
          <span className="fc-invites__control-label" id="fc-invite-uses">
            How many people can use it
          </span>
          <SegmentedControl<MaxUsesChoice>
            aria-labelledby="fc-invite-uses"
            value={maxUses}
            onChange={setMaxUses}
            options={[
              { value: "1", label: "1" },
              { value: "5", label: "5" },
              { value: "10", label: "10" },
              { value: "unlimited", label: "Any" },
            ]}
          />
        </div>

        <div className="fc-invites__control">
          <span className="fc-invites__control-label" id="fc-invite-expiry">
            Stops working after
          </span>
          <SegmentedControl<ExpiryChoice>
            aria-labelledby="fc-invite-expiry"
            value={expiry}
            onChange={setExpiry}
            options={[
              { value: "never", label: "Never" },
              { value: "24", label: "24 hours" },
              { value: "168", label: "7 days" },
              { value: "720", label: "30 days" },
            ]}
          />
        </div>
      </div>

      {create.isError && (
        <p className="fc-settings__error" role="alert">
          {errorMessage(create.error)}
        </p>
      )}

      <div className="fc-settings__actions">
        <Button
          variant="primary"
          loading={create.isPending && create.variables?.kind === "code"}
          onClick={() => create.mutate({ kind: "code" })}
        >
          Create an access code
        </Button>
        <Button
          variant="secondary"
          loading={create.isPending && create.variables?.kind === "link"}
          onClick={() => create.mutate({ kind: "link" })}
        >
          Create an invite link
        </Button>
      </div>

      {created && (
        <div className="fc-invite-result" role="status">
          <h3 className="fc-invite-result__title">
            {created.kind === "code"
              ? "Read this out to your friend"
              : "Send them this link"}
          </h3>
          <p className="fc-invite-result__code fc-tabular">
            {created.kind === "code" ? created.code : created.url}
          </p>
          <div className="fc-settings__actions">
            <Button
              variant="secondary"
              onClick={() =>
                void copy(
                  created.kind === "code" ? created.code : created.url,
                  created.kind === "code" ? "Code" : "Link",
                )
              }
            >
              Copy
            </Button>
            <Button variant="ghost" onClick={() => setCreated(null)}>
              Done
            </Button>
          </div>
          <p className="fc-invite-result__hint">
            You can copy this again from the list below at any time.
          </p>
        </div>
      )}

      <h3 className="fc-settings__subheading">Active invites</h3>

      {invites.isLoading ? (
        <div className="fc-invite-list" aria-hidden="true">
          {Array.from({ length: 2 }, (_, index) => (
            <Skeleton key={index} height="64px" radius="sm" />
          ))}
        </div>
      ) : (invites.data ?? []).length === 0 ? (
        <EmptyState
          icon={<TicketIcon />}
          title="No active invites"
          description="Nobody can join this group right now. Create a code or a link above and the group opens for exactly as long as you choose."
        />
      ) : (
        <ul className="fc-invite-list">
          {(invites.data ?? []).map((invite) => (
            <li className="fc-invite" key={invite.id}>
              <div className="fc-invite__body">
                <span className="fc-invite__code fc-tabular">
                  {inviteShareText(invite)}
                </span>
                <span className="fc-invite__meta fc-tabular">
                  {invite.kind === "code" ? "Code" : "Link"} · joins as{" "}
                  {ROLE_LABELS[invite.roleOnJoin].toLowerCase()} ·{" "}
                  {invite.maxUses === null
                    ? `used ${invite.usedCount} times`
                    : `${invite.usedCount} of ${invite.maxUses} used`}{" "}
                  ·{" "}
                  {invite.expiresAt === null
                    ? "never expires"
                    : `expires ${formatDate(invite.expiresAt)}`}
                </span>
              </div>

              <div className="fc-invite__actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    void copy(
                      inviteShareText(invite),
                      invite.kind === "link" ? "Link" : "Code",
                    )
                  }
                >
                  Copy
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={
                    revoke.isPending && revoke.variables?.inviteId === invite.id
                  }
                  onClick={() =>
                    revoke.mutate({ inviteId: invite.id, code: invite.code })
                  }
                >
                  Revoke
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ──────────────────────────────── Danger zone ─────────────────────────────── */

function DangerZone({
  groupId,
  groupName,
}: {
  groupId: string;
  groupName: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const preview = useQuery(
    trpc.groups.deletionPreview.queryOptions({ groupId }),
  );

  const [confirmName, setConfirmName] = useState("");
  const [totpCode, setTotpCode] = useState("");

  const destroy = useMutation({
    mutationFn: (input: { confirmName: string; totpCode: string }) =>
      trpcClient.groups.delete.mutate({ groupId, ...input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.list.queryKey(),
      });
      await navigate({ to: "/" });
      toast(`${groupName} has been deleted.`, { variant: "success" });
    },
  });

  const nameMatches = confirmName.trim() === groupName.trim();
  const codeLooksValid = /^\d{6}$/.test(totpCode);

  return (
    <section className="fc-danger" aria-labelledby="fc-danger">
      <div className="fc-settings__section-head">
        <h2
          className="fc-settings__section-title fc-danger__title"
          id="fc-danger"
        >
          Delete this group
        </h2>
      </div>

      {preview.isLoading ? (
        <Skeleton height="120px" radius="md" />
      ) : preview.isError || !preview.data ? (
        <p className="fc-settings__error" role="alert">
          {errorMessage(preview.error)}
        </p>
      ) : !preview.data.canDelete ? (
        <p className="fc-danger__blocked">
          Everyone else has to leave first. {preview.data.otherMembers}{" "}
          {preview.data.otherMembers === 1 ? "person is" : "people are"} still
          in this group. Remove{" "}
          {preview.data.otherMembers === 1 ? "them" : "them all"} above, then
          come back — this ordering is deliberate, so a group is never deleted
          out from under the people using it.
        </p>
      ) : (
        <>
          <p className="fc-danger__lead">
            This deletes, permanently and for everyone:
          </p>
          <ul className="fc-danger__list fc-tabular">
            <li>
              <strong>{preview.data.photoCount.toLocaleString()}</strong> photos
            </li>
            <li>
              <strong>{preview.data.videoCount.toLocaleString()}</strong> videos
            </li>
            <li>
              <strong>{preview.data.tripCount.toLocaleString()}</strong> trips
              and albums
            </li>
            <li>
              <strong>{preview.data.storageLabel}</strong> of stored files
            </li>
          </ul>

          {destroy.isError && (
            <p className="fc-settings__error" role="alert">
              {errorMessage(destroy.error)}
            </p>
          )}

          <form
            className="fc-danger__form"
            onSubmit={(event) => {
              event.preventDefault();
              destroy.mutate({ confirmName: confirmName.trim(), totpCode });
            }}
          >
            <Field
              label={`Type ${groupName} to confirm`}
              hint="It must match exactly, including capitals."
            >
              <Input
                value={confirmName}
                autoComplete="off"
                onChange={(event) => setConfirmName(event.target.value)}
              />
            </Field>

            <Field
              label="Authenticator code"
              hint="A six-digit code from your authenticator app. Deleting a group always asks for one."
            >
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={totpCode}
                onChange={(event) =>
                  setTotpCode(event.target.value.replace(/\D/g, ""))
                }
              />
            </Field>

            <Button
              type="submit"
              variant="danger"
              loading={destroy.isPending}
              disabled={!nameMatches || !codeLooksValid}
            >
              Delete {groupName} forever
            </Button>
          </form>
        </>
      )}
    </section>
  );
}

/* ─────────────────────────────────── Shared ───────────────────────────────── */

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** Dates cross the wire as strings but are typed as `Date`; accept either. */
function formatDate(value: Date | string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? "—" : DATE_FORMAT.format(date);
}

function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="5" r="1.75" />
      <circle cx="12" cy="12" r="1.75" />
      <circle cx="12" cy="19" r="1.75" />
    </svg>
  );
}

function TicketIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9.5V7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2.5a2.5 2.5 0 0 0 0 5V17a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2.5a2.5 2.5 0 0 0 0-5z" />
      <path d="M14 6v2M14 11v2M14 16v2" />
    </svg>
  );
}
